import React, { useEffect, useRef, useState, useMemo } from 'react';
import type { DatePickerEventValue, DatePickerProps } from '@interstate/components/DatePicker';
import { DatePicker } from '@interstate/components/DatePicker';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import type { TypographyVariant } from '@interstate/components/Typography/Types/TypographyTypes';
import { MaskedDatePickerWrapper, MaskTypography } from './maskInput.styles';
import { MASK_VALUE_DEFAULT } from './constants';

interface MaskDatePickerProps extends Omit<DatePickerProps, 'onChange'> {
    handleDatePickerOnChange: (field: string, event: InterstateOnChangeEvent<DatePickerEventValue>) => void;
    maskValue?: string;
    defaultMask: boolean;
    masking?: any;
    name: string;
    handleDatePickerBlur?: (field: string, event: InterstateOnChangeEvent<DatePickerEventValue>) => void;
}

const BASE_SIZE_VARIANTS: Record<string, TypographyVariant> = {
    sm: 'label-md',
    md: 'label-md',
    lg: 'label-lg'
} as const;

const SIZE_TO_VARIANT_MAP: Record<string, TypographyVariant> = {
    ...BASE_SIZE_VARIANTS,
    small: BASE_SIZE_VARIANTS.sm,
    medium: BASE_SIZE_VARIANTS.md,
    large: BASE_SIZE_VARIANTS.lg
} as const;

const getMaskTypographyVariant = (size: string): TypographyVariant => {
    return SIZE_TO_VARIANT_MAP[size];
};

type MaskStyleProperties = {
    top: string;
    left: string;
    width: string;
    height: string;
    fontSize: string;
    lineHeight: string;
};

const BASE_MASK_STYLES: Record<string, MaskStyleProperties> = {
    sm: {
        top: '2.29rem',
        left: '0.5rem',
        width: '65%',
        height: '1rem',
        fontSize: '1rem',
        lineHeight: '1rem'
    },
    md: {
        top: '2.65rem',
        left: '0.8rem',
        width: '65%',
        height: '1.143rem',
        fontSize: '1.143rem',
        lineHeight: '1.143rem'
    },
    lg: {
        top: '3.2rem',
        left: '1rem',
        width: '65%',
        height: '1.286rem',
        fontSize: '1.286rem',
        lineHeight: '1.286rem'
    }
} as const;

const MASK_STYLE_MAP: Record<string, MaskStyleProperties> = {
    ...BASE_MASK_STYLES,
    small: BASE_MASK_STYLES.sm,
    medium: BASE_MASK_STYLES.md,
    large: BASE_MASK_STYLES.lg
} as const;

const getMaskStyles = (size: string) => {
    return MASK_STYLE_MAP[size];
};

const MaskDatePicker: React.FC<MaskDatePickerProps> = ({
    handleDatePickerOnChange,
    handleDatePickerBlur,
    maskValue = MASK_VALUE_DEFAULT,
    defaultMask,
    value,
    name,
    masking: _masking,
    size = 'md',
    'data-testid': dataTestId = 'mask-datepicker',
    ...datePickerProps
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const dateIconClicked = useRef(false);
    const [localDateValue, setLocalDateValue] = useState<Date | undefined | null>(undefined);
    const [showMask, setShowMask] = useState<boolean>(defaultMask || Boolean(value));
    const latestValueRef = useRef(value);

    const maskTypographyVariant = useMemo(() => {
        return getMaskTypographyVariant(size);
    }, [size]);

    const maskStyles = useMemo(() => {
        return getMaskStyles(size);
    }, [size]);

    const handleInputChange = (e: InterstateOnChangeEvent<DatePickerEventValue>) => {
        const dateValue = e.target.value && e.target.value.dateValue ? e.target.value.dateValue : '';
        handleDatePickerOnChange(name, e);
        setLocalDateValue(dateValue ? new Date(dateValue) : undefined);
        if (dateIconClicked.current) {
            setShowMask(defaultMask || Boolean(dateValue));
        }
    };

    const handleOnBlur = (e: InterstateOnChangeEvent<DatePickerEventValue>) => {
        if (handleDatePickerBlur) {
            handleDatePickerBlur(name, e);
        }
        setShowMask(defaultMask || Boolean(value));
    };

    const handleMaskClick = (e: any) => {
        e.stopPropagation();
        containerRef.current?.querySelector('input')?.focus();
    };

    const handleInputFocus = () => {
        dateIconClicked.current = false;
        setLocalDateValue(null);
        setShowMask(false);
    };

    useEffect(() => {
        latestValueRef.current = value;
    }, [value]);

    const handleDateIconClick = (_e: any) => {
        dateIconClicked.current = true;
        setLocalDateValue(null);
        setShowMask(false);

        const handleOutsideClick = (e: MouseEvent) => {
            const calendarRef = document.querySelector('.MuiPickersPopper-root');
            const target = e.target as Node;
            if (calendarRef && !calendarRef.contains(target)) {
                setShowMask(Boolean(latestValueRef.current) || defaultMask);
            }
            document.removeEventListener('click', handleOutsideClick);
        };

        setTimeout(() => {
            document.addEventListener('click', handleOutsideClick);
        }, 0);
    };

    useEffect(() => {
        const inputRef = containerRef.current?.querySelector('input') ?? null;
        const dateIcon = containerRef.current?.querySelector('button.MuiIconButton-root') ?? null;

        if (!inputRef || !dateIcon) return;

        inputRef.addEventListener('focus', handleInputFocus);
        dateIcon.addEventListener('click', handleDateIconClick);

        return () => {
            inputRef.removeEventListener('focus', handleInputFocus);
            dateIcon.removeEventListener('click', handleDateIconClick);
        };
    }, []);

    useEffect(() => {
        if (showMask === true) {
            setLocalDateValue(null);
        }
    }, [showMask]);

    return (
        <MaskedDatePickerWrapper ref={containerRef} data-testid={`${dataTestId}-container`}>
            <DatePicker
                {...datePickerProps}
                size={size}
                onChange={handleInputChange}
                value={localDateValue}
                onBlur={handleOnBlur}
                data-testid={dataTestId}
            />

            <span onClick={handleMaskClick}>
                <MaskTypography
                    data-testid={`${dataTestId}-overlay`}
                    variant={maskTypographyVariant}
                    tag="span"
                    $isVisible={showMask}
                    $maskStyles={maskStyles}
                    sx={{ fontSize: `${maskStyles.fontSize} !important`, lineHeight: `${maskStyles.lineHeight} !important` }}
                >
                    {maskValue}
                </MaskTypography>
            </span>
        </MaskedDatePickerWrapper>
    );
};

export default MaskDatePicker;
